import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useEffect } from 'react';

// Resolve a CSS color to a concrete hex string by stuffing it onto a
// throwaway element and reading the computed style.  Capacitor's
// StatusBar plugin won't accept CSS vars or rgb()/hsl() strings — it
// needs a hex.  This converts whatever `--color-statusbar-bg`
// resolves to (which itself chases `--md-*` vars that swap with
// scheme) into something the plugin will accept.
const resolveCssColor = cssExpr => {
  if (typeof document === 'undefined') return null;
  const probe = document.createElement('div');
  probe.style.color = cssExpr;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  // computed is `rgb(r, g, b)` or `rgba(r, g, b, a)`; convert to hex.
  const match = computed.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;
  const [r, g, b] = match[1].split(',').map(s => Number(s.trim()));
  const toHex = n => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Push the active scheme + the resolved `--color-statusbar-bg` to
// the native status bar so it doesn't fight the app's chrome (light
// text on light bar, etc.).  Web is a no-op.
//
// Called from inside ThemeSchemeProvider so it re-fires whenever the
// user toggles the scheme.  No-op outside Capacitor native.
export const useStatusBarSync = scheme => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const apply = async () => {
      try {
        // Style.Light = light icons on a dark bar; Style.Dark = dark
        // icons on a light bar.  Confusing naming, but that's the API.
        const style = scheme === 'dark' ? Style.Light : Style.Dark;
        await StatusBar.setStyle({ style });
        const bg = resolveCssColor('var(--color-statusbar-bg)');
        if (bg) {
          await StatusBar.setBackgroundColor({ color: bg });
        }
      } catch {
        // Plugin unavailable on this platform — ignore.
      }
    };
    apply();
  }, [scheme]);
};
